import { DinamikBilesen } from '@service/fikoyt-ui/decorators/dinamik-bilesen';
import {
  ChangeDetectionStrategy, Component, computed, effect,
  input, model, signal, Type,
} from '@angular/core';
import { CommonModule, NgComponentOutlet } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { paramDeger } from '@/src/app/shared/utils/param-deger.utils';
import { FikoYTKokBilesenComponent } from '@/src/service/kok-bilesen/kok-bilesen.component';
import { DinamikBilesenRegistry } from '@service/fikoyt-ui/src/lib/dinamik-bilesen/dinamik-bilesen.registry';
import { FikoYTIstekKok } from '@/src/service/modules/fikoyt-istek-kok/fikoyt.istek.makinesi.service';
import { GenelStokEditComponent } from './genel-stok-edit.component';
import { OzelAlanGoruntuleyiciComponent } from '@service/fikoyt-ui/src/lib/ozel-alan/ozel-alan-goruntuleyici.component';
import { IOzelAlan } from '@service/fikoyt-ui/src/lib/ozel-alan/ozel-alan.types';

type Durum = 'kritik' | 'uyari' | 'normal';
const DURUM_BILGI: Record<Durum, { etiket: string; renk: string; rgb: string }> = {
  kritik: { etiket: 'Kritik', renk: '#ef4444', rgb: '239,68,68' },
  uyari: { etiket: 'Uyarı', renk: '#f59e0b', rgb: '245,158,11' },
  normal: { etiket: 'Normal', renk: '#22c55e', rgb: '34,197,94' },
};

type Tip = 'giris' | 'cikis' | 'sayim' | 'fire';
const TIP_BILGI: Record<Tip, { etiket: string; simge: string; renk: string; isaret: string }> = {
  giris: { etiket: 'Giriş', simge: '⬆', renk: '#22c55e', isaret: '+' },
  cikis: { etiket: 'Çıkış', simge: '⬇', renk: '#ef4444', isaret: '−' },
  sayim: { etiket: 'Sayım', simge: '⚖', renk: '#3b82f6', isaret: '=' },
  fire: { etiket: 'Fire', simge: '⚠', renk: '#f59e0b', isaret: '−' },
};

// ---------------------------------------------------------------------------
// genel-stok: kütük-stok.dinamik-bilesen.ts ile AYNI hareket/hesap desenini
// kullanır ama kütüğe özel değildir — kullanıcı isim/birim/limit + istediği
// kadar "Özel Alan" (ad:değer) tanımlayarak herhangi bir ürünü takip eder.
// kutuk-stok dosyalarına dokunulmamıştır; tamamen ayrı id/bileşen setiyle
// (genel-stok / genel-stok-hareket) kayıt olur.
// ---------------------------------------------------------------------------
@Component({
  selector: 'genel-stok-kart',
  standalone: true,
  imports: [CommonModule, NgComponentOutlet, OzelAlanGoruntuleyiciComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="gsk" [style.--c]="renk()">
      <div class="gsk-ust">
        <span class="gsk-ad">📦 {{ d('isim') || 'İsimsiz Ürün' }}</span>
        <span class="gsk-rozet"
          [style.background]="'rgba('+rgb()+',.15)'"
          [style.color]="renk()">{{ DURUM_BILGI[durum()].etiket }}</span>
      </div>

      <ozel-alan-goruntuleyici [alanlar]="ozelAlanlar()" />

      <div class="gsk-hizli">
        <span class="gsk-miktar">{{ kalanStok() }} <span class="gsk-birim">{{ d('birim') || 'adet' }}</span></span>
        <span class="gsk-hizli-sag">
          <input class="gsk-hizli-adet" type="number" min="1" [value]="hizliMiktar()"
            (input)="hizliMiktar.set(Math.max(1, +$any($event.target).value || 1))"
            [disabled]="_kaydediyor()" title="Hızlı hareket miktarı">
          <button class="gsk-hbtn gsk-eksi" (click)="hizliHareket('cikis')"
            [disabled]="_kaydediyor()" title="Hızlı çıkarma (alt hareket olarak kaydeder)">−</button>
          <button class="gsk-hbtn gsk-arti" (click)="hizliHareket('giris')"
            [disabled]="_kaydediyor()" title="Hızlı ekleme (alt hareket olarak kaydeder)">+</button>
        </span>
      </div>
      <div class="gsk-bar"><div class="gsk-bar-ic" [style.width.%]="barYuzde()" [style.background]="renk()"></div></div>
      <div class="gsk-limit-etiket">kritik {{ d('kritikLimit') || '—' }} · uyarı {{ d('uyariLimit') || '—' }}</div>

      @if (altHareketler().length) {
        <div class="gsk-alt-baslik">Son Hareketler</div>
        <div class="gsk-alt-liste">
          @for (altKart of altHareketler(); track altKart._id) {
            @if (altBilesen()) {
              <ng-container
                [ngComponentOutlet]="altBilesen()!"
                [ngComponentOutletInputs]="{ parametreler: altKart }">
              </ng-container>
            }
          }
        </div>
      }
    </div>
  `,
  styles: [`
    .gsk { display:flex; flex-direction:column; gap:6px; padding:10px 12px;
      border:1px solid var(--py-kenarlik,#e5e7eb); border-radius:10px; background:var(--py-arkaplan,#fff); }
    .gsk-ust { display:flex; align-items:center; justify-content:space-between; gap:8px; }
    .gsk-ad { font-size:13px; font-weight:700; color:var(--py-metin,#333); }
    .gsk-rozet { padding:2px 8px; border-radius:4px; font-size:10px; font-weight:700;
      text-transform:uppercase; letter-spacing:.04em; white-space:nowrap; }
    .gsk-miktar { font-size:20px; font-weight:800; color:var(--py-metin,#222); }
    .gsk-birim { font-size:11px; font-weight:500; color:var(--py-metin-ikincil,#888); }
    .gsk-bar { height:5px; border-radius:3px; background:rgba(0,0,0,.06); overflow:hidden; }
    .gsk-bar-ic { height:100%; border-radius:3px; transition:width .5s; }
    .gsk-limit-etiket { font-size:10px; color:var(--py-metin-ucuncul,#999); }
    .gsk-alt-baslik { font-size:10px; font-weight:700; text-transform:uppercase;
      letter-spacing:.06em; color:var(--py-metin-ucuncul,#999); margin-top:4px; }
    .gsk-alt-liste { display:flex; flex-direction:column; gap:4px; }
    .gsk-hizli { display:flex; align-items:center; justify-content:space-between; gap:8px; }
    .gsk-hizli-sag { display:flex; align-items:center; gap:4px; }
    .gsk-hizli-adet { width:46px; padding:2px 4px; font-size:12px; text-align:center;
      border:1px solid var(--py-kenarlik,#e5e7eb); border-radius:6px;
      background:var(--py-arkaplan,#fff); color:var(--py-metin,#333); }
    .gsk-hbtn { width:26px; height:26px; border:none; border-radius:8px; cursor:pointer;
      font-size:15px; font-weight:800; line-height:1; color:#fff;
      display:flex; align-items:center; justify-content:center;
      transition:filter .15s, transform .05s; }
    .gsk-hbtn:hover:not(:disabled) { filter:brightness(1.12); }
    .gsk-hbtn:active:not(:disabled) { transform:scale(.92); }
    .gsk-hbtn:disabled { opacity:.45; cursor:not-allowed; }
    .gsk-arti { background:#22c55e; }
    .gsk-eksi { background:#ef4444; }
  `],
})
export class GenelStokKartComponent extends FikoYTKokBilesenComponent {
  parametreler = input<any>();
  _hesap    = input<any>();

  constructor() {
    super();
    effect(() => {
      DinamikBilesenRegistry.goruntumAl('genel-stok-hareket', 'paylasilmis_kart_sade')
        .then(tip => this._altBilesen.set(tip));
    }, { allowSignalWrites: true });
    effect(() => {
      const kart = this.parametreler();
      const id = kart?._id || kart?.kart?._id;
      if (id) this._hesapCek(id);
    });
  }

  private _altBilesen = signal<Type<any> | null>(null);
  altBilesen = this._altBilesen.asReadonly();
  private _hesapYerel = signal<any>(null);
  private _ekleIstek: FikoYTIstekKok | null = null;

  protected readonly Math = Math;
  hizliMiktar = signal(1);
  _kaydediyor = signal(false);

  d(ismi: string) { return paramDeger(this.parametreler()?.ek, ismi); }

  /** Kullanıcının serbestçe eklediği alanlar — sabit şema yok, tür bazlı (metin/sayı/tarih/onay/seçim/renk) */
  ozelAlanlar = computed<IOzelAlan[]>(() => {
    const v = this.parametreler()?.ek?.ozelAlanlar;
    return Array.isArray(v) ? v.filter((a: any) => a?.ad) : [];
  });

  hesapYerel = computed(() => {
    const p = this.parametreler();
    const gelen = p?.kart?._hesap ?? this._hesap() ?? p?._hesap ?? {};
    const canli = this._hesapYerel();
    if (canli != null) return canli;
    return gelen || {};
  });

  async hizliHareket(tip: 'giris' | 'cikis') {
    const p = this.parametreler();
    const parentId = p?._id || p?.kart?._id;
    if (!parentId || this._kaydediyor()) return;
    const miktar = Math.max(1, +(this.hizliMiktar() || 0));
    this._kaydediyor.set(true);
    try {
      const aciklama = tip === 'giris' ? 'Hızlı ekleme (+)' : 'Hızlı çıkarma (−)';
      const sahip = (this.FikoYT as any)?.kimlik?.aktifKullaniciJson()?._id;
      const kartKimlik = this.FikoYT.fikoIdUret();
      const icerikKimlik = this.FikoYT.fikoIdUret();

      const yeniKart: any = {
        _id: kartKimlik, vt: 'FikoYT.sistem.hareketler', yol: 'FikoYT.sistem.hareketler.kalem',
        turu: 'KART', gizlilik: 'Herkese Açık', _uid: parentId, sahip, tarih: new Date(),
        veri: {
          solSimge: { baslik: tip === 'giris' ? '⬆' : '⬇', renkArka: tip === 'giris' ? '#22c55e' : '#ef4444', renkYazi: '#fff' },
          icerikler: [icerikKimlik],
        },
      };
      const yeniIcerik: any = {
        _id: icerikKimlik, _uid: kartKimlik, html: '', aciklama: '', sifre: null,
        ek: {
          turu: 'UYGULAMAICERIK', _secilenSetId: 'genel-stok.hareket', bilesen: 'genel-stok-hareket',
          parametreler: [
            { ismi: 'tip', degeri: tip },
            { ismi: 'miktar', degeri: miktar },
            { ismi: 'aciklama', degeri: aciklama },
          ],
          tip, miktar, aciklama,
        },
      };

      if (!this._ekleIstek) {
        this._ekleIstek = new FikoYTIstekKok({
          metod: 'POST', komut: 'FikoYT.sistem.DB.hareketler.ekle',
          onEkOlsun: true, depoModu: false, oncelik: 0,
          gecikmeliCevap: true, gecikmeliCevapSuresi: 500,
        });
      }
      const fdKart = new FormData(); fdKart.append('req_body', JSON.stringify(yeniKart));
      this._ekleIstek.parametreler = fdKart;
      await this._ekleIstek.isteSync();
      const fdIc = new FormData(); fdIc.append('req_body', JSON.stringify(yeniIcerik));
      this._ekleIstek.parametreler = fdIc;
      await this._ekleIstek.isteSync();

      this._hesapYerel.set(null);
      this._hesapCek(parentId);
    } catch (e) {
      console.warn('[GenelStok] Hızlı hareket kaydedilemedi:', parentId, e);
    } finally {
      this._kaydediyor.set(false);
    }
  }

  kalanStok = computed(() => {
    const h = this.hesapYerel().kalanStok;
    return h != null ? Math.round(h * 100) / 100 : 0;
  });

  altHareketler = computed<any[]>(() => this.hesapYerel().altHareketler ?? []);

  /** Prepipe/persist _hesap gelmediyse DB'den taze aggregate ile kalan + son 5 hareketi çek */
  private async _hesapCek(kartId: string) {
    try {
      const istek = new FikoYTIstekKok({ metod: 'POST', komut: 'FikoYT.sistem.DB.hareketler.aggregate' });
      istek.parametreler = { aggregate: [
        { $match: { _id: kartId } },
        { $lookup: { from: 'hareketler', let: { pid: '$_id' }, pipeline: [
          { $match: { $expr: { $eq: ['$_uid', '$$pid'] }, silindi: { $ne: true } } },
          { $lookup: { from: 'hareketler', localField: 'veri.icerikler', foreignField: '_id', as: 'ic' } },
          { $unwind: { path: '$ic', preserveNullAndEmptyArrays: false } },
          { $match: { 'ic.ek._secilenSetId': 'genel-stok.hareket' } },
          { $sort: { _id: 1 } },
          { $project: {
            _id: '$ic._id',
            tip: { $ifNull: ['$ic.ek.tip', { $let: { vars: { p: { $arrayElemAt: [ { $filter: { input: { $ifNull: ['$ic.ek.parametreler', []] }, as: 'x', cond: { $eq: ['$$x.ismi', 'tip'] } } }, 0 ] } }, in: { $ifNull: ['$$p.degeri', 'giris'] } } } ] },
            miktar: { $toDouble: { $ifNull: ['$ic.ek.miktar', { $let: { vars: { p: { $arrayElemAt: [ { $filter: { input: { $ifNull: ['$ic.ek.parametreler', []] }, as: 'x', cond: { $eq: ['$$x.ismi', 'miktar'] } } }, 0 ] } }, in: { $ifNull: ['$$p.degeri', 0] } } } ] } },
            aciklama: { $ifNull: ['$ic.ek.aciklama', { $let: { vars: { p: { $arrayElemAt: [ { $filter: { input: { $ifNull: ['$ic.ek.parametreler', []] }, as: 'x', cond: { $eq: ['$$x.ismi', 'aciklama'] } } }, 0 ] } }, in: { $ifNull: ['$$p.degeri', ''] } } } ] },
            ek: '$ic.ek',
          } },
        ], as: '_alt' } },
        { $addFields: {
          kalanStok: {
            $reduce: {
              input: '$_alt', initialValue: 0,
              in: { $switch: { branches: [
                { case: { $eq: ['$$this.tip', 'giris'] }, then: { $add: ['$$value', '$$this.miktar'] } },
                { case: { $in: ['$$this.tip', ['cikis', 'fire']] }, then: { $subtract: ['$$value', '$$this.miktar'] } },
                { case: { $eq: ['$$this.tip', 'sayim'] }, then: '$$value' },
              ], default: '$$value' } } },
          },
          altHareketler: { $slice: ['$_alt', -5] },
        } },
        { $project: { _alt: 0 } },
      ] };
      const cevap: any = await istek.isteSync();
      const hesap = cevap?.cevap?.sonuc?.[0];
      if (hesap && (hesap.kalanStok != null || hesap.altHareketler)) {
        this._hesapYerel.set({ kalanStok: hesap.kalanStok ?? 0, altHareketler: hesap.altHareketler ?? [] });
      }
    } catch {}
  }

  durum = computed<Durum>(() => {
    const kritik = +(this.d('kritikLimit') || 0);
    const uyari  = +(this.d('uyariLimit')  || 0);
    const kalan  = this.kalanStok();
    if (kritik && kalan <= kritik) return 'kritik';
    if (uyari  && kalan <= uyari)  return 'uyari';
    return 'normal';
  });

  DURUM_BILGI = DURUM_BILGI;
  renk = computed(() => DURUM_BILGI[this.durum()].renk);
  rgb  = computed(() => DURUM_BILGI[this.durum()].rgb);

  barYuzde = computed(() => {
    const uyari = +(this.d('uyariLimit') || 0);
    const tavan = uyari > 0 ? uyari * 1.6 : Math.max(this.kalanStok(), 1);
    return Math.max(4, Math.min(100, (this.kalanStok() / tavan) * 100));
  });
}

// ---------------------------------------------------------------------------
// genel-stok DinamikBilesen kaydı
// ---------------------------------------------------------------------------
@DinamikBilesen({
  id: 'genel-stok',
  baslik: 'Genel Stok',
  aciklama: 'Kullanıcı tanımlı özel alanlarla genel amaçlı stok kalemi ve kalan miktar takibi',
  ikon: 'appstore',
  kategori: 'genel-stok',
})
class GenelStok {
  paylas_kutusu     = GenelStokEditComponent;
  paylasilmis_kart      = GenelStokKartComponent;
  paylasilmis_kart_sade = GenelStokKartComponent;

  hesaplamalar = {
    prepipe: [
      { $lookup: { from: 'hareketler', let: { pid: '$_id' }, pipeline: [
        { $match: { $expr: { $eq: ['$_uid', '$$pid'] }, silindi: { $ne: true } } },
        { $lookup: { from: 'hareketler', localField: 'veri.icerikler', foreignField: '_id', as: 'ic' } },
        { $unwind: { path: '$ic', preserveNullAndEmptyArrays: false } },
        { $match: { 'ic.ek._secilenSetId': 'genel-stok.hareket' } },
        { $sort: { _id: 1 } },
        { $project: {
          _id: '$ic._id',
          tip: { $ifNull: ['$ic.ek.tip', { $let: { vars: { p: { $arrayElemAt: [ { $filter: { input: { $ifNull: ['$ic.ek.parametreler', []] }, as: 'x', cond: { $eq: ['$$x.ismi', 'tip'] } } }, 0 ] } }, in: { $ifNull: ['$$p.degeri', 'giris'] } } } ] },
          miktar: { $toDouble: { $ifNull: ['$ic.ek.miktar', { $let: { vars: { p: { $arrayElemAt: [ { $filter: { input: { $ifNull: ['$ic.ek.parametreler', []] }, as: 'x', cond: { $eq: ['$$x.ismi', 'miktar'] } } }, 0 ] } }, in: { $ifNull: ['$$p.degeri', 0] } } } ] } },
          aciklama: { $ifNull: ['$ic.ek.aciklama', { $let: { vars: { p: { $arrayElemAt: [ { $filter: { input: { $ifNull: ['$ic.ek.parametreler', []] }, as: 'x', cond: { $eq: ['$$x.ismi', 'aciklama'] } } }, 0 ] } }, in: { $ifNull: ['$$p.degeri', ''] } } } ] },
          ek: '$ic.ek',
        } },
      ], as: '_altHareket' } },
      { $addFields: {
        '_hesap.kalanStok': {
          $reduce: {
            input: '$_altHareket',
            initialValue: 0,
            in: { $switch: { branches: [
              { case: { $eq: ['$$this.tip', 'giris'] },   then: { $add:      ['$$value', '$$this.miktar'] } },
              { case: { $in: ['$$this.tip', ['cikis','fire']] }, then: { $subtract: ['$$value', '$$this.miktar'] } },
              { case: { $eq: ['$$this.tip', 'sayim'] },   then: '$$value' },
            ], default: '$$value' } },
          },
        },
        '_hesap.sonHareketTipi': { $ifNull: [{ $arrayElemAt: ['$_altHareket.tip', -1] }, null] },
        '_hesap.altHareketler': { $slice: ['$_altHareket', -5] },
      } },
      { $project: { _altHareket: 0 } },
    ],
  };
}

// ---------------------------------------------------------------------------
// Hareket sade kartı
// ---------------------------------------------------------------------------
@Component({
  selector: 'genel-stok-hareket-kart-sade',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="gshk" [style.--tr]="bilgi().renk">
      <span class="gshk-ikon">{{ bilgi().simge }}</span>
      <span class="gshk-tip">{{ bilgi().etiket }}</span>
      <span class="gshk-miktar">{{ miktarYazimi() }}</span>
      @if (d('aciklama')) { <span class="gshk-aciklama">{{ d('aciklama') }}</span> }
    </div>
  `,
  styles: [`
    .gshk { display:flex; align-items:center; gap:8px; padding:6px 10px;
      border-left:3px solid var(--tr,#94a3b8); border-radius:0 8px 8px 0;
      background:color-mix(in srgb, var(--tr) 6%, transparent); }
    .gshk-ikon   { font-size:16px; }
    .gshk-tip    { font-size:13px; font-weight:700; color:var(--tr,#333); text-transform:uppercase; letter-spacing:.5px; }
    .gshk-miktar { font-size:13px; font-weight:700; font-family:monospace; color:var(--py-metin,#333); }
    .gshk-aciklama { font-size:12px; color:var(--py-metin-ikincil,#666); margin-left:auto; }
  `],
})
export class GenelStokHareketKartSadeComponent extends FikoYTKokBilesenComponent {
  parametreler = input<any>();
  constructor() { super(); }
  p    = computed(() => this.parametreler()?.ek || {});
  d(alan: string): any { return paramDeger(this.p(), alan); }
  tip = computed(() => {
    const p = this.parametreler();
    const ek = p?.ek || {};
    const d = ek?.tip != null && ek?.tip !== '' ? ek.tip : paramDeger(ek, 'tip');
    return (d || 'giris') as Tip;
  });
  bilgi = computed(() => TIP_BILGI[this.tip()] || TIP_BILGI.giris);
  miktarYazimi = computed(() => {
    const m = +(this.d('miktar') || 0);
    const t = this.tip();
    if (t === 'cikis' || t === 'fire') return `-${m}`;
    if (t === 'giris') return `+${m}`;
    return `${m}`;
  });
}

// ---------------------------------------------------------------------------
// Hareket edit bileşeni (paylaş kutusu)
// ---------------------------------------------------------------------------
@Component({
  selector: 'genel-stok-hareket-edit',
  standalone: true,
  imports: [CommonModule, FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="gshe">
      <div class="gshe-baslik">🔄 Hareket Tipi</div>
      <div class="gshe-butonlar">
        @for (t of tipler; track t) {
          <button class="gshe-btn" [class.gshe-aktif]="p()?.tip === t"
            [style.--tr]="TIP_BILGI[t].renk" (click)="sec(t)">
            {{ TIP_BILGI[t].simge }} {{ TIP_BILGI[t].etiket }}
          </button>
        }
      </div>
      <div class="gshe-row">
        <div class="gshe-alan" style="flex:0 0 140px">
          <label class="gshe-etiket">Miktar</label>
          <input class="gshe-inp" type="number" min="0"
            [ngModel]="p()?.miktar" (ngModelChange)="guncelle('miktar', $event)" placeholder="Miktar"/>
        </div>
        <div class="gshe-alan" style="flex:1">
          <label class="gshe-etiket">Açıklama</label>
          <input class="gshe-inp" type="text"
            [ngModel]="p()?.aciklama || ''" (ngModelChange)="guncelle('aciklama', $event)"
            placeholder="Açıklama (isteğe bağlı)"/>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .gshe { display:flex; flex-direction:column; gap:8px; padding:4px 0; }
    .gshe-baslik { font-size:13px; font-weight:600; color:var(--py-metin-ucuncul,#999); }
    .gshe-butonlar { display:flex; gap:4px; flex-wrap:wrap; }
    .gshe-btn { padding:6px 12px; border:1px solid var(--py-kenarlik,#e5e7eb); border-radius:6px;
      background:transparent; cursor:pointer; font-size:12px; font-weight:500;
      color:var(--py-metin-ikincil,#666); transition:all .15s;
      &:hover { border-color:var(--tr); color:var(--tr); } }
    .gshe-aktif { border-color:var(--tr) !important; color:var(--tr) !important;
      background:color-mix(in srgb, var(--tr) 12%, transparent) !important; font-weight:700; }
    .gshe-row { display:flex; gap:10px; }
    .gshe-alan { display:flex; flex-direction:column; gap:2px; }
    .gshe-etiket { font-weight:500; font-size:12px; color:var(--py-metin-ucuncul, rgba(0,0,0,.45)); }
    .gshe-inp { padding:6px 8px; border:1px solid var(--py-kenarlik,#e5e7eb); border-radius:6px;
      font-size:13px; background:var(--py-arkaplan,#fff); color:var(--py-metin,#333); }
  `],
})
export class GenelStokHareketEditComponent extends FikoYTKokBilesenComponent {
  parametreler = model<any>();
  tipler: Tip[] = ['giris', 'cikis', 'sayim', 'fire'];
  TIP_BILGI = TIP_BILGI;
  constructor() { super(); }
  p = computed(() => this.parametreler()?.ek || {});

  private parametreYaz(alan: string, deger: any) {
    const params = this.parametreler();
    if (!params?.ek) return;
    const ek = params.ek;
    ek[alan] = deger;
    if (Array.isArray(ek.parametreler)) {
      const mevcut = ek.parametreler.find((x: any) => x.ismi === alan);
      if (mevcut) mevcut.degeri = deger;
    }
    this.parametreler.set({ ...params });
  }

  sec(tip: Tip) { this.parametreYaz('tip', tip); }
  guncelle(alan: string, deger: any) { this.parametreYaz(alan, deger); }
}

// ---------------------------------------------------------------------------
// genel-stok-hareket DinamikBilesen kaydı
// ---------------------------------------------------------------------------
@DinamikBilesen({
  id: 'genel-stok-hareket',
  baslik: 'Genel Stok Hareketi',
  aciklama: 'Giriş / Çıkış / Sayım / Fire hareketi',
  ikon: 'swap',
  kategori: 'genel-stok',
})
class GenelStokHareket {
  paylas_kutusu         = GenelStokHareketEditComponent;
  paylasilmis_kart_sade = GenelStokHareketKartSadeComponent;
}
